import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { wsService, WsMessage } from '../services/websocket'
import { useLocalStorage } from '../hooks/useLocalStorage'
import { useUrlParams } from '../hooks/useUrlParams'
import { useUser } from './UserContext'

interface SessionFeatures {
  storyteller?: boolean
  [key: string]: boolean | undefined
}

interface WebSocketContextType {
  connected: boolean
  sessionId: string
  sessionFeatures: SessionFeatures
  sessionCharacter: string
  sessionStory: string
  sessionMode: string
  sessionTransmission: string
  send: (msg: object) => void
  onMessage: (type: string, handler: (msg: WsMessage) => void) => () => void
}

const WebSocketContext = createContext<WebSocketContextType>(null!)

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  const [sessionId, setSessionId] = useLocalStorage('sessionId', '')
  const [sessionFeatures, setSessionFeatures] = useState<SessionFeatures>({})
  const [sessionCharacter, setSessionCharacter] = useState('')
  const [sessionStory, setSessionStory] = useState('')
  const [sessionMode, setSessionMode] = useState('')
  const [sessionTransmission, setSessionTransmission] = useState('')
  const urlParams = useUrlParams()
  const { userApiKey } = useUser()

  useEffect(() => {
    const unsubStatus = wsService.onStatusChange(setConnected)
    const unsubSession = wsService.on('session', (msg) => {
      if (msg.message) {
        setSessionId(msg.message)

        const params = new URLSearchParams(window.location.search)
        params.set('session_id', msg.message)
        window.history.replaceState(null, '', `?${params}${window.location.hash}`)
      }
      const data = msg.data as { features?: SessionFeatures; character?: string; story?: string; mode?: string; transmission?: string } | undefined
      if (data?.features) {
        setSessionFeatures(data.features)
      }
      if (data?.character !== undefined) setSessionCharacter(data.character)
      if (data?.story !== undefined) setSessionStory(data.story)
      if (data?.mode !== undefined) setSessionMode(data.mode)
      if (data?.transmission !== undefined) setSessionTransmission(data.transmission)
    })

    wsService.connect({
      sessionId: urlParams.sessionId || sessionId || undefined,
      mode: urlParams.mode,
      transmission: urlParams.transmission,
      character: urlParams.character,
      story: urlParams.story,
      features: urlParams.features,
      apiKey: userApiKey || undefined,
    })

    return () => {
      unsubStatus()
      unsubSession()
      wsService.disconnect()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onMessage = useCallback((type: string, handler: (msg: WsMessage) => void) => {
    return wsService.on(type, handler)
  }, [])

  const send = useCallback((msg: object) => wsService.send(msg), [])

  return (
    <WebSocketContext.Provider value={{ connected, sessionId, sessionFeatures, sessionCharacter, sessionStory, sessionMode, sessionTransmission, send, onMessage }}>
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => useContext(WebSocketContext)
